import { DateTime } from "luxon"
import { isPlaceholderPartecipant, paymentCoveredElsewhere } from "./func.utils"
import { isMatch } from "./match.utils"

// ──────────────────────────────────────────────────────────────────────────
// A CHI SI FATTURA
//
// Le lezioni le fattura sempre il maestro: quello che cambia è a chi. Le
// private le fattura a chi viene a lezione, e l'importo lo decide il suo
// listino; le altre — SAT, CAD — le fattura al circolo a ore, e chi viene
// non paga niente perché ha l'abbonamento.
//
// Su Firestore, nella parte privata del coach, che legge solo lui:
//   coaches/{coachID}/private/settings.billingData = {
//       siro: { sat: { "2026-09-01": 14 }, cad: { "2026-09-01": 16 } },
//   }
//
// Un tipo che sta qui dentro è da fatturare al circolo; uno che non c'è è
// da fatturare ai privati. Le tariffe sono in €/h e vanno per data, come il
// costo dei campi: cambiare quella di oggi non tocca i mesi già fatturati.
// Le tariffe scritte prima erano un numero secco, senza data: si leggono
// come se fossero sempre state in vigore.
// ──────────────────────────────────────────────────────────────────────────
export const BILLED_TO_PLAYERS = "players"
export const BILLED_TO_SCHOOL = "school"

export const getSchoolRates = (billingData, schoolID) => {
	const rates = billingData?.[schoolID]
	return rates && typeof rates === "object" ? rates : null
}

export const isSchoolBilledType = (rates, lessonType) =>
	!!rates && Object.prototype.hasOwnProperty.call(rates, lessonType)

// A chi è stata fatturata questa lezione. Sta scritto sulla lezione dal
// giorno in cui l'app ha imparato a chiederlo: spostare un tipo da un
// gruppo all'altro vale da lì in avanti, e i riepiloghi già chiusi non si
// muovono. Le lezioni di prima non hanno niente scritto, e valgono come
// sono sempre valse — cioè come dice il listino di adesso.
export const getLessonBilledTo = (lesson, rates) => {
	if (lesson?.billedTo === BILLED_TO_SCHOOL || lesson?.billedTo === BILLED_TO_PLAYERS)
		return lesson.billedTo

	return isSchoolBilledType(rates, lesson?.lessonType) ? BILLED_TO_SCHOOL : BILLED_TO_PLAYERS
}

// ──────────────────────────────────────────────────────────────────────────
// LE LEZIONI IN PIÙ MAESTRI
//
// Un corso si fa anche in due o tre: il titolare (`coachID`) e gli
// assistenti (`assistantCoachIDs`). Le ore sono di tutti, e un corso
// fatturato al circolo lo fattura ognuno alla sua tariffa. Quello che pagano
// gli allievi invece lo incassa il titolare, e il campo lo paga lui: per
// l'assistente non sono né incassi, né costi, né pagamenti da segnare.
// ──────────────────────────────────────────────────────────────────────────
export const isAssistantIn = (lesson, coachID) =>
	Boolean(coachID) &&
	lesson?.coachID !== coachID &&
	Array.isArray(lesson?.assistantCoachIDs) &&
	lesson.assistantCoachIDs.includes(coachID)

// ──────────────────────────────────────────────────────────────────────────
// COSTO CAMPI
//
// Su Firestore, nel documento della scuola (o del coach come fallback):
//   schools/{schoolID}.courtsFee = {
//       tennis: {
//           base:   { "2026-08-01": 10, "2026-09-01": 12 },
//           lights: { "2026-09-01": 14 },
//           heat:   { "2026-09-01": 16 },
//       },
//   }
//
// Ogni chiave è la data da cui la tariffa oraria entra in vigore e resta
// valida finché non ne compare una successiva. Se la scuola non ha nessun
// courtsFee non viene conteggiato nessun costo campo.
//
// `base` è il campo e basta. `lights` e `heat` sono quanto costa il campo
// con la luce o con il riscaldamento — il prezzo pieno, come lo scrive il
// circolo — e ci sono solo dove il circolo li fa pagare a parte: è la loro
// presenza a far comparire l'interruttore quando si scrive una lezione,
// perché un interruttore che non cambia nessun prezzo non serve a niente.
// Quando sono accesi tutti e due i due rincari si sommano sul prezzo base:
// 12 + (14 − 12) + (16 − 12) = 18.
//
// `noHeat` è il vecchio nome di `base`, da quando il riscaldamento era
// l'unica cosa che cambiava il prezzo: le tariffe salvate allora continuano
// a valere senza che nessuno le riscriva.
// ──────────────────────────────────────────────────────────────────────────
const toISODate = date => {
	if (!date) return null
	if (DateTime.isDateTime(date)) return date.toISODate()
	if (date instanceof Date) return DateTime.fromJSDate(date).toISODate()
	if (typeof date?.toDate === "function") return DateTime.fromJSDate(date.toDate()).toISODate()
	if (typeof date === "string") return date.split("T")[0]
	return null
}

// Cerca nella tabella la tariffa in vigore alla data indicata
const pickFeeAtDate = (feeTable, isoDate) => {
	if (!feeTable || !isoDate) return null

	const effectiveFrom = Object.keys(feeTable)
		.filter(from => from <= isoDate)
		.sort()
		.pop()

	if (effectiveFrom === undefined) return null

	const fee = Number(feeTable[effectiveFrom])
	return Number.isFinite(fee) ? fee : null
}

// Quanto vale un'ora al circolo alla data della lezione. Una tariffa
// scritta prima che esistessero le date è un numero secco, e vale sempre.
export const getBillingRateAtDate = (rates, lessonType, date) => {
	const rate = rates?.[lessonType]
	if (rate === undefined || rate === null) return null
	if (typeof rate === "number") return Number.isFinite(rate) ? rate : null

	return pickFeeAtDate(rate, toISODate(date))
}

// La tariffa che vale oggi: è quella che il listino fa vedere e quella che
// si riscrive quando cambia.
export const getBillingRateToday = (rates, lessonType) =>
	getBillingRateAtDate(rates, lessonType, new Date())

const hasFees = feeTable => !!feeTable && Object.keys(feeTable).length > 0

// I supplementi che questo circolo fa pagare per questo sport: ci sono se
// c'è un prezzo diverso, e dove non c'è non si mostra nessun interruttore.
export const getCourtExtras = (courtsFee, sportID) => {
	const sportFees = courtsFee?.[sportID]

	return {
		lights: hasFees(sportFees?.lights),
		heat: hasFees(sportFees?.heat),
	}
}

// Quanto costa il campo all'ora in una lezione: il prezzo base, più il
// rincaro di ogni supplemento acceso. Un supplemento che il circolo non fa
// pagare non ha tabella, e non aggiunge niente.
export const getCourtFeeAtDate = (courtsFee, sportID, { usingLights, usingHeat } = {}, date) => {
	const sportFees = courtsFee?.[sportID]
	if (!sportFees) return null

	const isoDate = toISODate(date)
	const baseFee = pickFeeAtDate(sportFees.base ?? sportFees.noHeat, isoDate)

	const extras = [
		usingLights ? pickFeeAtDate(sportFees.lights, isoDate) : null,
		usingHeat ? pickFeeAtDate(sportFees.heat, isoDate) : null,
	].filter(fee => fee !== null)

	// senza prezzo base c'è solo quello del supplemento acceso, ed è già
	// il prezzo pieno: è il caso di chi ha scritto una tariffa sola
	if (baseFee === null) return extras.length > 0 ? Math.max(...extras) : null

	return extras.reduce((total, fee) => total + Math.max(0, fee - baseFee), baseFee)
}

// ──────────────────────────────────────────────────────────────────────────
// CHI PAGA IL CAMPO
//
// In una lezione privata il campo può pagarlo il maestro — l'allievo paga
// il prezzo pieno e il campo si scala dal riepilogo — oppure gli allievi,
// direttamente al circolo: allora il maestro chiede meno, tanto quanto la
// quota di campo di ognuno, e dal riepilogo non c'è niente da scalare.
//
// La regola di solito la dà il circolo (`courtPaidBy` tra le condizioni del
// maestro), ma la singola lezione può fare diversamente: la scelta viene
// scritta sulla lezione, così cambiare la regola oggi non riscrive i
// riepiloghi di ieri. Le lezioni salvate prima non hanno niente scritto, e
// valgono come sono sempre valse: campo a carico del maestro.
// ──────────────────────────────────────────────────────────────────────────
export const COURT_PAID_BY_COACH = "coach"
export const COURT_PAID_BY_PLAYERS = "players"

export const isCourtPaidByPlayers = courtPaidBy => courtPaidBy === COURT_PAID_BY_PLAYERS

// ──────────────────────────────────────────────────────────────────────────
// CHI INCASSA I SOSTITUTI
//
// Chi viene la volta al posto di un abbonato in un corso paga qualcuno: il
// circolo direttamente, o il maestro, che quei soldi li tiene e a fine mese
// li dà al circolo. Lo decide il circolo per ogni corso, e la lezione se lo
// scrive sul sostituto quando lo segna: cambiare domani la regola non deve
// riscrivere chi ha incassato oggi.
//
// Nessuno dei due è un compenso del maestro: quello che incassa il circolo
// non passa da lui, e quello che incassa lui è del circolo. Il secondo però
// va ricordato, e il riepilogo lo tiene da parte.
// ──────────────────────────────────────────────────────────────────────────
export const SUBSTITUTE_PAID_TO_COACH = "coach"
export const SUBSTITUTE_PAID_TO_SCHOOL = "school"

// I corsi in abbonamento: al SAT e al CAD si viene con l'abbonamento, e chi
// viene la volta al posto di un abbonato segue la regola qui sopra. Stanno
// qui e non coi sostituti perché è da qui che i conti devono saperlo.
export const SUBSCRIPTION_COURSE_TYPES = ["sat", "cad"]

export const isSubscriptionCourse = lessonTypeID => SUBSCRIPTION_COURSE_TYPES.includes(lessonTypeID)

// Chi in un corso paga la volta, e allora i suoi soldi sono del circolo: chi
// li ha presi lo dice `paidTo`, non la regola degli allievi.
//
// Il sostituto, sempre. E chi viene in più senza abbonamento in un corso che
// si fattura al circolo — un CAD da tre più uno che paga: gli abbonati hanno
// pagato il circolo, e lui paga il circolo allo stesso modo. Questo secondo
// lo si riconosce da `paidTo`, che il modulo gli scrive da quando lo chiede:
// prima, in una lezione del circolo, agli allievi non si scriveva niente.
export const paysCourseVisit = (player, lesson) => {
	if (!isSubscriptionCourse(lesson?.lessonType)) return false
	if (player?.substituteFor) return true
	return (
		lesson?.billedTo === BILLED_TO_SCHOOL &&
		!paymentCoveredElsewhere(player?.paymentMethod) &&
		(player?.paidTo === SUBSTITUTE_PAID_TO_COACH || player?.paidTo === SUBSTITUTE_PAID_TO_SCHOOL)
	)
}

// Chi ha pagato la volta per conto del circolo: i suoi soldi non sono del
// maestro, chiunque li abbia in tasca.
const paidForSchool = (player, lesson) =>
	paysCourseVisit(player, lesson) &&
	(player.paidTo === SUBSTITUTE_PAID_TO_SCHOOL || player.paidTo === SUBSTITUTE_PAID_TO_COACH)

// Soldi che il maestro ha incassato per conto del circolo, da dargli
export const heldForSchool = (player, lesson) =>
	paysCourseVisit(player, lesson) &&
	player.paidTo === SUBSTITUTE_PAID_TO_COACH &&
	(Number(player.paymentAmount) || 0) > 0

// ──────────────────────────────────────────────────────────────────────────
// CHI PAGA IL CIRCOLO INVECE DEL MAESTRO
//
// Capita che un allievo non paghi il maestro ma il circolo: in segreteria,
// con il POS del circolo. Quei soldi il maestro non li incassa. Il circolo
// se li tiene, ne trattiene la quota di campo dell'allievo — se il campo lo
// paga il maestro — e il resto lo deve al maestro, che glielo fattura.
//
// È il caso speculare dei sostituti incassati dal maestro: lì il maestro ha
// in tasca soldi del circolo, qui il circolo ha in tasca soldi del maestro.
//
// Si scrive sul partecipante, `paidTo: "school"`, come per i sostituti dei
// corsi. Di solito lo decide la regola del maestro in quel circolo
// (`playersPayTo` tra le sue condizioni), ma ogni allievo di ogni lezione
// può fare diversamente. Chi non ha niente scritto ha pagato il maestro: è
// così che le lezioni di prima sono sempre andate.
//
// Chi nei corsi paga la volta resta fuori: per lui `paidTo` dice chi incassa
// soldi che sono comunque del circolo, e vale la regola qui sopra.
// ──────────────────────────────────────────────────────────────────────────
export const PAID_TO_COACH = "coach"
export const PAID_TO_SCHOOL = "school"

export const isPaidToSchool = paidTo => paidTo === PAID_TO_SCHOOL

// Chi paga il circolo un metodo di pagamento non ce l'ha: dove si tiene la
// scelta di come ha pagato un allievo, al posto del metodo si segna questo.
export const PAID_TO_SCHOOL_CHOICE = "paidToSchool"
export const PAID_TO_SCHOOL_LABEL = "Al circolo"

// Un allievo che in questa lezione ha pagato il circolo invece del maestro
export const paidSchoolInstead = (player, lesson) =>
	isPaidToSchool(player?.paidTo) && !paysCourseVisit(player, lesson)

// Chi i soldi li ha dati al circolo, per l'uno o per l'altro motivo: è
// quello che si scrive accanto al suo nome, al posto di come ha pagato.
export const paidAtSchool = (player, lesson) =>
	paidSchoolInstead(player, lesson) ||
	(paysCourseVisit(player, lesson) && player.paidTo === SUBSTITUTE_PAID_TO_SCHOOL)

// I partecipanti i cui soldi li incassa il maestro: non chi ha pagato per
// conto del circolo, né chi ha pagato il circolo al posto suo.
export const isCoachIncome = (player, lesson) =>
	!paidForSchool(player, lesson) && !paidSchoolInstead(player, lesson)

// Chi in questa lezione ti deve dei soldi e non ha scritto come li paga: a
// fine mese finirebbe sotto "Non specificato" senza sapere da dove viene.
// Non contano le lezioni fatturate al circolo, dove nessun allievo paga, i
// posti ancora senza nome, chi era assente, i sostituti che hanno pagato
// per conto del circolo e chi ha pagato il circolo: lì "al circolo" basta.
export const partecipantsWithoutPaymentMethod = (lesson, rates) => {
	if (getLessonBilledTo(lesson, rates) === BILLED_TO_SCHOOL) return []
	return (lesson?.partecipants ?? []).filter(
		player =>
			!isPlaceholderPartecipant(player) &&
			isCoachIncome(player, lesson) &&
			player.isPresent !== false &&
			!player.paymentMethod,
	)
}

// Solo le lezioni già iniziate: una lezione di domani non si è ancora
// pagata, e segnarla adesso vorrebbe dire avere il mese intero in rosso.
export const lacksPaymentMethod = (lesson, rates, now = new Date()) => {
	// una lezione annullata non ha niente da far pagare
	if (lesson?.cancelled === true) return false
	const start = lesson?.lessonStart?.toDate?.()
	if (!start || start > now) return false
	return partecipantsWithoutPaymentMethod(lesson, rates).length > 0
}

const roundCents = value => Math.round(value * 100) / 100

// La quota di campo di ogni allievo: quello che il campo costa per tutta la
// lezione, diviso per quanti la fanno. È quello che il maestro non chiede
// più, quando il campo lo pagano loro.
export const getCourtShare = (
	courtsFee,
	{ sportID, usingLights, usingHeat, date, hours, playersCount },
) => {
	if (!(hours > 0) || !(playersCount > 0)) return 0

	const fee = getCourtFeeAtDate(courtsFee, sportID, { usingLights, usingHeat }, date)
	if (fee === null) return 0

	return roundCents((fee * hours) / playersCount)
}

// Quanto si chiede davvero all'allievo: il prezzo di listino meno la sua
// quota di campo, quando il campo lo paga lui. Sotto zero non si scende.
export const getPlayerPrice = (listPrice, courtShare) =>
	Math.max(0, roundCents((Number(listPrice) || 0) - (Number(courtShare) || 0)))

// Le lezioni fuori dai campi (racchette in classe, centro estivo) vengono
// salvate con courtNumber 0: non c'è nessun campo da pagare. Le lezioni
// vecchie senza campo salvato restano invece a carico del coach, come prima.
const hasNoCourt = lesson => {
	const court = lesson?.court
	if (court === undefined || court === null) return false
	return Number(court?.courtNumber ?? court) === 0
}

export const getLessonHours = lesson => {
	const start = lesson?.lessonStart?.toDate?.()
	const end = lesson?.lessonEnd?.toDate?.()
	if (!start || !end) return 0

	return DateTime.fromJSDate(end).diff(DateTime.fromJSDate(start), "hours").toObject().hours ?? 0
}

// ──────────────────────────────────────────────────────────────────────────
// ACCUMULATORE DEL RIEPILOGO ECONOMICO
// ──────────────────────────────────────────────────────────────────────────
export const emptyEarnings = () => ({
	schoolBilling: { byType: {}, total: 0, hours: 0, hoursWithoutRate: 0 },
	privateIncome: { byType: {}, byPlayer: {}, total: 0, hours: 0 },
	courtCosts: {
		byFee: {},
		total: 0,
		hours: 0,
		lessonsWithoutFee: 0,
		lessonsPaidByPlayers: 0,
		// le quote di campo che il circolo si è tenuto da chi ha pagato lui:
		// il campo l'hai pagato tu, ma quella parte ce l'aveva già il circolo
		withheldBySchool: 0,
	},
	// incassato dal maestro per conto del circolo: non è suo, e non sta nel
	// netto, ma va ricordato per darglielo
	heldForSchool: { total: 0, byPlayer: {} },
	// pagato al circolo dagli allievi delle private, tolta la quota di campo
	// che il circolo si è già tenuto: è del maestro, e va fatturato al circolo
	paidToSchool: { total: 0, byPlayer: {} },
	// il campo delle partite che giochi tu: non è lavoro, ma il campo lo paghi
	// tu, e nel riepilogo sta nel costo campi insieme a quello delle lezioni
	matches: { byFee: {}, total: 0, hours: 0, count: 0, withoutFee: 0 },
	net: 0,
})

// Le ore si sommano per prezzo pagato: campo liscio, con la luce, col
// riscaldamento o con tutti e due sono righe diverse del riepilogo.
const addCourtHours = (byFee, { usingLights, usingHeat, fee, hours }) => {
	const feeKey = `${usingLights ? "lights" : ""}${usingHeat ? "heat" : ""}base_${fee}`
	const feeEntry = byFee[feeKey] ?? { usingLights, usingHeat, fee, hours: 0, cost: 0 }
	feeEntry.hours += hours
	feeEntry.cost += fee * hours
	byFee[feeKey] = feeEntry
}

// Una partita: il campo intero, alla tariffa di quel giorno. Senza una
// tariffa la partita si conta lo stesso, per dire che manca il prezzo
// invece di farla sparire.
const accumulateMatch = (acc, lesson, { courtsFee, hours }) => {
	acc.matches.count += 1
	acc.matches.hours += hours

	const usingLights = !!lesson.usingLights
	const usingHeat = !!lesson.usingHeat
	const fee = courtsFee
		? getCourtFeeAtDate(courtsFee, lesson.sportID, { usingLights, usingHeat }, lesson.lessonStart)
		: null

	if (fee === null) {
		acc.matches.withoutFee += 1
		return
	}

	addCourtHours(acc.matches.byFee, { usingLights, usingHeat, fee, hours })
	acc.matches.total += fee * hours
}

const accumulateHeldForSchool = (acc, lesson) => {
	;(lesson.partecipants ?? []).filter(player => heldForSchool(player, lesson)).forEach(player => {
		const amount = Number(player.paymentAmount) || 0
		const key = player.playerID || player.playerName || "unknownPlayer"
		const entry = acc.heldForSchool.byPlayer[key] ?? {
			playerName: player.playerName ?? "Non specificato",
			amount: 0,
			lessons: 0,
			substitutions: 0,
		}
		entry.amount += amount
		entry.lessons += 1
		if (player.substituteFor) entry.substitutions += 1
		acc.heldForSchool.byPlayer[key] = entry
		acc.heldForSchool.total += amount
	})
}

// La quota di campo di ogni allievo di una privata, quando il campo lo paga
// il maestro. Senza listino campi, senza campo o col campo pagato dagli
// allievi non c'è niente che il circolo possa trattenere.
const coachCourtShare = (lesson, { courtsFee, sportID, hours }) => {
	if (!courtsFee || hasNoCourt(lesson) || isCourtPaidByPlayers(lesson.courtPaidBy)) return 0

	return getCourtShare(courtsFee, {
		sportID,
		usingLights: !!lesson.usingLights,
		usingHeat: !!lesson.usingHeat,
		date: lesson.lessonStart,
		hours,
		// i sostituti prendono il posto di un assente: non dividono il campo
		playersCount: (lesson.partecipants ?? []).filter(player => !player?.substituteFor).length,
	})
}

// Chi ha pagato il circolo invece del maestro: quello che ha pagato, meno la
// sua quota di campo, è da fatturare al circolo. Torna quanto campo si è
// già tenuto il circolo, che il maestro non deve più contare tra i costi.
//
// Il circolo trattiene la quota, ma non più di quello che ha ricevuto: un
// allievo che ha pagato meno della sua quota lascia il resto del campo a
// carico del maestro, invece di una fattura in negativo. Chi era assente non
// ha pagato niente, e il suo campo resta del maestro.
const accumulatePaidToSchool = (acc, lesson, courtShare) => {
	let withheld = 0

	;(lesson.partecipants ?? []).forEach(player => {
		if (!paidSchoolInstead(player, lesson)) return
		if (isPlaceholderPartecipant(player) || player.isPresent === false) return

		const paid = Number(player.paymentAmount) || 0
		if (paid <= 0) return

		const kept = Math.min(courtShare, paid)
		const amount = roundCents(paid - kept)
		withheld += kept

		const key = player.playerID || player.playerName || "unknownPlayer"
		const entry = acc.paidToSchool.byPlayer[key] ?? {
			playerName: player.playerName ?? "Non specificato",
			paid: 0,
			courtShare: 0,
			amount: 0,
			lessons: 0,
		}
		entry.paid += paid
		entry.courtShare += kept
		entry.amount += amount
		entry.lessons += 1
		acc.paidToSchool.byPlayer[key] = entry
		acc.paidToSchool.total += amount
	})

	return withheld
}

// `coachID` è di chi si fa il riepilogo: dove è assistente, conta solo quello
// che fattura al circolo.
export const accumulateEarnings = (acc, lesson, { rates, courtsFee, coachID }) => {
	const lessonType = lesson.lessonType
	const sportID = lesson.sportID || "unknownSport"
	const hours = getLessonHours(lesson)
	if (!lessonType || hours <= 0) return
	// annullata: non si è fatta, non si fattura e non si incassa
	if (lesson.cancelled === true) return

	// una partita non si fattura e non si incassa: ha solo il campo
	if (isMatch(lesson)) {
		accumulateMatch(acc, lesson, { courtsFee, hours })
		return
	}

	const isAssistant = isAssistantIn(lesson, coachID)

	// i sostituti li ha incassati il titolare, ed è lui a doverli al circolo
	if (!isAssistant) accumulateHeldForSchool(acc, lesson)

	if (getLessonBilledTo(lesson, rates) === BILLED_TO_SCHOOL) {
		// ore da fatturare al circolo, alla tariffa in vigore quel giorno
		const rate = getBillingRateAtDate(rates, lessonType, lesson.lessonStart)

		if (rate === null) {
			// il tipo è uscito dal gruppo del circolo, o la tariffa comincia
			// dopo: le ore ci sono, il prezzo no, e va detto invece di
			// contarle zero in silenzio
			acc.schoolBilling.hoursWithoutRate += hours
			return
		}

		const typeEntry = acc.schoolBilling.byType[lessonType] ?? { hours: 0, amount: 0, rate }
		typeEntry.hours += hours
		typeEntry.amount += rate * hours
		// due tariffe diverse nello stesso periodo: il dettaglio non può
		// mostrarne una sola come se valesse per tutte
		typeEntry.rate = typeEntry.rate === rate ? rate : null
		acc.schoolBilling.byType[lessonType] = typeEntry
		acc.schoolBilling.hours += hours
		acc.schoolBilling.total += rate * hours
		return
	}

	// lezione privata: incassi e campo sono del titolare
	if (isAssistant) return

	// lezione privata: incasso diretto dai clienti + costo del campo
	// i sostituti pagati per conto del circolo non sono un incasso del maestro,
	// e nemmeno chi ha pagato il circolo invece del maestro
	const coachPartecipants = (lesson.partecipants ?? []).filter(player =>
		isCoachIncome(player, lesson),
	)
	const amount = coachPartecipants.reduce(
		(sum, player) => sum + (Number(player.paymentAmount) || 0),
		0,
	)

	const typeEntry = acc.privateIncome.byType[lessonType] ?? { hours: 0, amount: 0 }
	typeEntry.hours += hours
	typeEntry.amount += amount
	acc.privateIncome.byType[lessonType] = typeEntry
	acc.privateIncome.hours += hours
	acc.privateIncome.total += amount
	coachPartecipants.forEach(player => {
		// un posto ancora senza nome non è un allievo da mettere in elenco
		if (isPlaceholderPartecipant(player)) return
		const playerID = player.playerID || player.playerName || "unknownPlayer"
		const playerEntry = acc.privateIncome.byPlayer[playerID] ?? {
			playerName: player.playerName ?? "Non specificato",
			hours: 0,
			amount: 0,
			lessons: 0,
		}
		playerEntry.hours += hours
		playerEntry.amount += Number(player.paymentAmount) || 0
		playerEntry.lessons += 1
		acc.privateIncome.byPlayer[playerID] = playerEntry
	})

	// chi ha pagato il circolo: da fatturargli, tolto il campo che si è tenuto
	const withheldBySchool = accumulatePaidToSchool(
		acc,
		lesson,
		coachCourtShare(lesson, { courtsFee, sportID, hours }),
	)

	// nessun listino campi per questa scuola → il campo non lo pago io
	if (!courtsFee) return

	// lezioni senza campo (racchette in classe, centro estivo) non hanno
	// nessun costo campo da scalare
	if (hasNoCourt(lesson)) return

	// il campo l'hanno pagato gli allievi al circolo: non è un costo mio, e
	// quello che ho chiesto loro era già al netto della loro quota
	if (isCourtPaidByPlayers(lesson.courtPaidBy)) {
		acc.courtCosts.lessonsPaidByPlayers += 1
		return
	}

	const usingLights = !!lesson.usingLights
	const usingHeat = !!lesson.usingHeat
	const fee = getCourtFeeAtDate(courtsFee, sportID, { usingLights, usingHeat }, lesson.lessonStart)

	if (fee === null) {
		acc.courtCosts.lessonsWithoutFee += 1
		return
	}

	addCourtHours(acc.courtCosts.byFee, { usingLights, usingHeat, fee, hours })
	acc.courtCosts.hours += hours
	// il campo costa quello che costa, ma la parte che il circolo si è già
	// tenuta da chi ha pagato lui non esce più dalle tue tasche
	acc.courtCosts.withheldBySchool += withheldBySchool
	acc.courtCosts.total += fee * hours - withheldBySchool
}

// Il campo si toglie tutto, anche quello di quando giochi tu: il costo
// campi è uno, e il netto è quello che resta davvero in tasca.
export const finalizeEarnings = acc => {
	acc.net =
		acc.schoolBilling.total +
		acc.paidToSchool.total +
		acc.privateIncome.total -
		acc.courtCosts.total -
		acc.matches.total
	return acc
}

// Il costo campi per tipo di campo (liscio, con la luce, col riscaldamento,
// con tutti e due) e dentro ognuno chi l'ha usato: le lezioni e le tue
// partite. Le ore dello stesso tipo pagate a due tariffe diverse, perché il
// circolo ha cambiato prezzo a metà periodo, restano due pezzi: il conto si
// deve poter rifare.
export const COURT_KINDS = ["base", "lights", "heat", "lightsHeat"]

const courtKind = ({ usingLights, usingHeat }) => {
	if (usingLights && usingHeat) return "lightsHeat"
	if (usingLights) return "lights"
	if (usingHeat) return "heat"
	return "base"
}

export const groupCourtCostsByKind = ({ courtCosts, matches } = {}) => {
	const groups = {}

	const add = (byFee, who) =>
		Object.values(byFee ?? {}).forEach(entry => {
			const kind = courtKind(entry)
			const group = groups[kind] ?? {
				kind,
				usingLights: !!entry.usingLights,
				usingHeat: !!entry.usingHeat,
				total: 0,
				lessons: null,
				matches: null,
			}
			const part = group[who] ?? { hours: 0, cost: 0, fees: [] }
			part.hours += entry.hours
			part.cost += entry.cost
			part.fees.push({ fee: entry.fee, hours: entry.hours })
			group[who] = part
			group.total += entry.cost
			groups[kind] = group
		})

	add(courtCosts?.byFee, "lessons")
	add(matches?.byFee, "matches")

	return COURT_KINDS.filter(kind => groups[kind]).map(kind => {
		const group = groups[kind]
		group.lessons?.fees.sort((a, b) => a.fee - b.fee)
		group.matches?.fees.sort((a, b) => a.fee - b.fee)
		return group
	})
}

// Arrotonda a 2 decimali e usa la virgola come separatore (12.5 → 12,5)
export const formatAmount = value => {
	const rounded = Math.round((Number(value) || 0) * 100) / 100
	return `${rounded}`.replace(".", ",")
}
